import { Injectable } from '@angular/core';
import { Recommendation } from '../types/session.type';
import { Observable } from 'rxjs/Rx';
import { Http } from '@angular/http';
import { TrackingService } from './tracking.service';

export class ZeissPanelValue {
  constructor(public name: string, public value: string | number) {}
}

export interface ZeissPanel {
  html: string;
  cssClass: string;
  values: ZeissPanelValue[];
  selected?: boolean;
}

export interface ZeissPanelGroup {
  type: string;
  condition: string;
  selected?: number;
  panels: ZeissPanel[];
}

export interface LenseSwatchGroup {
  label: string;
  swatches: string[];
}

export interface Recommendations {
  [key: string]: Recommendation;
}
export interface RecommendationTracking {
  name: string;
  label: any;
  dimension: string;
  dimensionValue: any;
}

@Injectable()
export class SessionService {
  hoursOutdoor: number;
  percentOutdoor: number;
  hoursDigital: number;
  percentDigital: number;

  age: ZeissPanelGroup;
  type: ZeissPanelGroup;
  symptom: ZeissPanelGroup;
  activity: ZeissPanelGroup;

  customLens: string;

  swatchGroups: LenseSwatchGroup[];

  allGlasses: Recommendation[];
  filteredGlasses: Recommendation[];
  recommendations: Recommendations;

  firstName: any;
  lastName: any;
  customerEmail: any;
  sendDoctor: any;
  doctor: any;
  phone: any;

  customer: any[];

  downloadURL: string;

  recommendationTrackingValues: RecommendationTracking[] = [];

  constructor(private http: Http, private trackingService: TrackingService) {}

  init(): Observable<void> {
    return new Observable<void>((observer) => {
      this.firstName = {
        model: null,
        placeholder: 'first name',
        name: 'firstName',
        type: 'text',
        required: false,
      };

      this.lastName = {
        model: null,
        placeholder: 'last name',
        name: 'lastName',
        type: 'text',
        required: false,
      };

      this.customerEmail = {
        model: null,
        placeholder: 'email',
        name: 'customerEmail',
        type: 'text',
        required: true,
      };

      this.doctor = {
        model: null,
        placeholder: 'doctor email',
        name: 'doctor',
        type: 'text',
        required: false,
        disabled: true,
      };

      this.phone = {
        model: null,
        name: 'phone',
        type: 'tel',
        required: true,
        placeholder: '*Enter number with no spaces or dashes ',
      };

      this.sendDoctor = {
        model: false,
        name: 'sendDoctor',
        type: 'toggle',
        label: 'Send to my doctor',
        selected: false,
        options: [
          {
            index: 0,
            label: null,
            value: false,
          },
          {
            index: 1,
            label: null,
            value: true,
          },
        ],
      };

      this.customer = [this.customerEmail, this.firstName, this.lastName, this.sendDoctor, this.doctor];

      this.customLens = 'Sunglasses';

      this.percentOutdoor = 0.5;
      this.percentDigital = 0.5;
      this.hoursDigital = 12;

      this.type = {
        type: 'checkbox',
        condition: 'AND',
        panels: [
          {
            html: `Near`,
            cssClass: 'title1',
            values: [new ZeissPanelValue('type_1', 'Near')],
          },
          {
            html: `Far Away`,
            cssClass: 'title2',
            values: [new ZeissPanelValue('type_3', 'Far Away')],
          },
          {
            html: `Using Computers`,
            cssClass: 'title3',
            values: [new ZeissPanelValue('type_2', 'Using Computers')],
          },
          {
            html: `Driving`,
            cssClass: 'title4',
            values: [new ZeissPanelValue('type_4', 'Driving')],
          },
        ],
      };

      this.age = {
        type: 'radio',
        condition: 'AND',
        selected: null,
        panels: [
          {
            html: `Under 18`,
            cssClass: 'title1',
            values: [new ZeissPanelValue('age_min', '0')],
          },
          {
            html: `18-30`,
            cssClass: 'title2',
            values: [new ZeissPanelValue('age_min', '18'), new ZeissPanelValue('age_max', '30')],
          },
          {
            html: `31-44`,
            cssClass: 'title3',
            values: [new ZeissPanelValue('age_min', '31'), new ZeissPanelValue('age_max', '44')],
          },
          {
            html: `45+`,
            cssClass: 'title4',
            values: [new ZeissPanelValue('age_min', '45')],
          },
        ],
      };

      this.symptom = {
        type: 'checkbox',
        condition: 'OR',
        selected: null,
        panels: [
          {
            html: `Scratches, Smudges or Reflections`,
            cssClass: 'title1',
            values: [new ZeissPanelValue('symptom_4', 'Scratches')],
          },
          {
            html: `Frequent Exposure to UV Light`,
            cssClass: 'title2',
            values: [new ZeissPanelValue('symptom_5', 'UV Exposure')],
          },
          {
            html: `Difficulty with Night Driving`,
            cssClass: 'title3',
            values: [new ZeissPanelValue('symptom_6', 'Night Driving')],
          },
          {
            html: `Tired Eyes, Stiff Neck or Headaches`,
            cssClass: 'title4',
            values: [
              new ZeissPanelValue('symptom_1', 'Tired Eyes'),
              new ZeissPanelValue('symptom_2', 'Stiff Neck'),
              new ZeissPanelValue('symptom_3', 'Headaches'),
            ],
          },
        ],
      };

      this.activity = {
        type: 'checkbox',
        condition: 'OR',
        selected: null,
        panels: [
          {
            html: `Frequent gaze shifts from phone to nearby objects`,
            cssClass: 'title1',
            values: [new ZeissPanelValue('activity_1', 'Phone to nearby')],
          },
          {
            html: `Frequent gaze shifts from phone to mid or far objects`,
            cssClass: 'title2',
            values: [new ZeissPanelValue('activity_2', 'Phone to mid')],
          },
          {
            html: `Frequent use of phone while on-the-move`,
            cssClass: 'title3',
            values: [new ZeissPanelValue('activity_3', 'On-the-move')],
          },
          {
            html: `Frequent gaze shifts from phone to computer or TV`,
            cssClass: 'title4',
            values: [new ZeissPanelValue('activity_4', 'Phone to computer')],
          },
        ],
      };

      this.swatchGroups = [
        {
          label: 'Polarized',
          swatches: ['008_108164_0_GrayPol85', 'farb_454_108124_0_BrownPol85', 'farb_474_108158_0_PioneerPol85'],
        },
        {
          label: 'Tints',
          swatches: [
            '008_108164_0_Gray85',
            '038_108116_0_Gray25',
            '069_108094_0_Pistachio25',
            '070_108113_0_Cinnamon25',
            '071_108121_0_Stone25',
            '072_108145_0_Jeans25',
            '091_108152_0_Rose',
            '101_108166_0_SkyletFun70',
            '102_108150_0_SkyletRoad80',
            'farb_456_brown25_flat_50',
            'farb_458_brown75_flat_50',
            'farb_459_brown90_flat_50',
            'farb_462_gray60_flat_50',
            'farb_480_blue12_flat_50',
            'Z_ECP_app_Skylet_Brown_2a',
          ],
        },
        {
          label: 'Mirror',
          swatches: [
            'farb_Mirror_Blue_50',
            'farb_Mirror_Bronze_50',
            'farb_Mirror_Gold_50',
            'farb_Mirror_Green_50',
            'farb_Mirror_Red_50',
            'farb_Mirror_Silver_50',
            'farb_Mirror_StrongBlue_50',
            'farb_Mirror_White_50',
            'Z_ECP_app_mirrors_Cool_Blue_2a',
            'Z_ECP_app_mirrors_Flash_Amber_2b',
            'Z_ECP_app_mirrors_Flash_Jade_2a',
            'Z_ECP_app_mirrors_Flash_Ruby_2a',
            'Z_ECP_app_mirrors_Flash_Sapphire_2b',
          ],
        },
      ];

      this.recommendations = {};

      const filePath = './assets/data/Zeiss-ECP-3.0-DB.tsv';

      this.http.get(filePath).subscribe(
        (response) => {
          this.allGlasses = this.toJSON(response.text(), '\t').map((recommendation: any) => {
            return new Recommendation(recommendation);
          });
          observer.next();
          observer.complete();
        },
        (error) => {
          observer.error();
        }
      );
    });
  }

  private toJSON(data, divider): any {
    const lineEndings = ['\r', '\n', '\r\n'];

    let newLine;
    lineEndings.forEach((lineEnding) => {
      if (data.indexOf(lineEnding) > -1) {
        newLine = lineEnding;
      }
    });

    const lines = data.split(newLine);
    const result = [];
    const headers = lines[0].split(divider);

    for (let i = 1; i < lines.length; i++) {
      const obj = {};
      let skipLine = false;
      const currentline = lines[i].split(divider);
      for (let j = 0; j < headers.length; j++) {
        if (currentline.length > 1) {
          obj[headers[j].trim()] = currentline[j].trim();
        } else {
          skipLine = true;
        }
      }
      if (!skipLine) {
        result.push(obj);
      }
    }

    return result;
  }

  private getGroupValues(groupKey: string): ZeissPanelValue[] {
    let values: ZeissPanelValue[] = [];
    switch (this[groupKey].type) {
      case 'checkbox':
        this[groupKey].panels.forEach((panel: ZeissPanel) => {
          if (panel.selected) {
            values = values.concat(panel.values);
          }
        });
        break;

      case 'radio':
        if (this[groupKey].selected || this[groupKey].selected === 0) {
          values = this[groupKey].panels[this[groupKey].selected].values;
        }
        break;

      default:
        break;
    }
    return values;
  }

  saveRecommendations(): void {
    const recommendationTracking = {};
    this.filteredGlasses.forEach((recommendation: Recommendation) => {
      this.recommendations[recommendation.pair] = recommendation;
      recommendationTracking[recommendation.pair] = recommendation.image;
    });
    this.trackEvent('recommendation', { key: 'Eyeglasses', value: recommendationTracking['Eyeglasses'] });
    this.trackEvent('recommendation', { key: 'Sunglasses', value: recommendationTracking['Sunglasses'] });
  }

  private filterByAge() {
    const ageValue = Number(this.getGroupValues('age').find((value) => value.name === 'age_min').value);
    this.filteredGlasses = this.filteredGlasses.filter((glasses) => {
      return glasses.age_min === ageValue;
    });
    this.trackEvent('age', ageValue);
  }

  private trackOutdoorsHours() {
    this.trackEvent('percent-outdoors', this.percentOutdoor);
  }

  private filterByTypeAndSymptom() {
    const types: ZeissPanelValue[] = this.getGroupValues('type');
    const symptoms: ZeissPanelValue[] = this.getGroupValues('symptom');
    const activities: ZeissPanelValue[] = this.getGroupValues('activity');

    const value1: string = types.find((value) => value.name === 'type_1')
      ? types.find((value) => value.name === 'type_1').value.toString()
      : 'n/a';
    const value2: string = types.find((value) => value.name === 'type_2')
      ? types.find((value) => value.name === 'type_2').value.toString()
      : 'n/a';
    const value3: string = types.find((value) => value.name === 'type_3')
      ? types.find((value) => value.name === 'type_3').value.toString()
      : 'n/a';
    const value4: string = types.find((value) => value.name === 'type_4')
      ? types.find((value) => value.name === 'type_4').value.toString()
      : 'n/a';

    const symptom1: string = symptoms.find((value) => value.name === 'symptom_1')
      ? symptoms.find((value) => value.name === 'symptom_1').value.toString()
      : 'n/a';
    const symptom4: string = symptoms.find((value) => value.name === 'symptom_4')
      ? symptoms.find((value) => value.name === 'symptom_4').value.toString()
      : 'n/a';
    const symptom5: string = symptoms.find((value) => value.name === 'symptom_5')
      ? symptoms.find((value) => value.name === 'symptom_5').value.toString()
      : 'n/a';
    const symptom6: string = symptoms.find((value) => value.name === 'symptom_6')
      ? symptoms.find((value) => value.name === 'symptom_6').value.toString()
      : 'n/a';

    const activity_1: string = activities.find((value) => value.name === 'activity_1')
      ? activities.find((value) => value.name === 'activity_1').value.toString()
      : 'n/a';
    const activity_2: string = activities.find((value) => value.name === 'activity_2')
      ? activities.find((value) => value.name === 'activity_2').value.toString()
      : 'n/a';
    const activity_3: string = activities.find((value) => value.name === 'activity_3')
      ? activities.find((value) => value.name === 'activity_3').value.toString()
      : 'n/a';
    const activity_4: string = activities.find((value) => value.name === 'activity_4')
      ? activities.find((value) => value.name === 'activity_4').value.toString()
      : 'n/a';

    this.filteredGlasses = this.filteredGlasses.filter((glasses, index) => {
      if (glasses.ignore_types === 1) {
        return true;
      }

      if (glasses.age_min === 0 && glasses.type_1 && glasses.symptom_1) {
        return (glasses.type_1 === value2 || glasses.symptom_1 === symptom1) && this.hoursDigital > 4;
      }

      if (glasses.age_min === 18 && glasses.type_1) {
        return glasses.type_1 === value2;
      } else if (glasses.age_min === 18 && glasses.symptom_1) {
        return glasses.symptom_1 === symptom1;
      }

      if (glasses.age_min === 31 && glasses.type_1 && glasses.type_2) {
        return glasses.type_1 === value2 && glasses.type_2 === value1;
      } else if (glasses.age_min === 31 && glasses.type_2) {
        return glasses.type_2 === value1;
      } else if (glasses.age_min === 31 && glasses.type_1) {
        return glasses.type_1 === value2;
      } else if (glasses.age_min === 31 && glasses.type_3 && glasses.type_3 && glasses.symptom_4) {
        return (
          glasses.type_3 === value3 ||
          glasses.type_4 === value4 ||
          glasses.symptom_4 === symptom4 ||
          glasses.symptom_5 === symptom5 ||
          glasses.symptom_6 === symptom6 ||
          glasses.activity_1 === activity_1 ||
          glasses.activity_2 === activity_2 ||
          glasses.activity_3 === activity_3 ||
          glasses.activity_4 === activity_4
        );
      } else if (glasses.age_min === 31 && !glasses.symptom_1 && !glasses.type_2 && !glasses.type_2) {
        return true;
      } else if (glasses.age_min === 31 && (glasses.type_1 || glasses.symptom_1)) {
        return glasses.type_1 === value2 || glasses.symptom_1 === symptom1;
      }

      if (glasses.age_min === 45 && glasses.type_1) {
        return glasses.type_1 === value2;
      }
    });
    this.trackEvent('type', types);
    this.trackEvent('symptom', symptoms);
    this.trackEvent('activity', activities);
  }

  private filterByDigitalHours() {
    this.filteredGlasses = this.filteredGlasses.filter((glasses) => {
      return this.hoursDigital >= Number(glasses.hours_min) && this.hoursDigital < Number(glasses.hours_max);
    });
    this.trackEvent('digital-hours', this.hoursDigital);
  }

  getRecommendations(): Observable<RecommendationTracking[]> {
    return new Observable<RecommendationTracking[]>((observer) => {
      this.filteredGlasses = this.allGlasses;
      this.filterByAge();
      this.filterByTypeAndSymptom();
      this.filterByDigitalHours();
      this.trackOutdoorsHours();
      this.saveRecommendations();
      console.log(this.trackingService.recommendationTrackingValues);
      observer.next(this.trackingService.recommendationTrackingValues);
      observer.complete();
    });
  }

  setCircleValues(key: string, value: number): void {
    this[`percent${key}`] = value / 100;
    this[`hours${key}`] = 24 * this[`percent${key}`];
  }

  trackEvent(eventLabel, value) {
    this.trackingService.track(eventLabel, value);
  }

  getAge(ageValue) {
    switch (ageValue) {
      case 45:
        return '>45';
      case 31:
        return '31-44';
      case 18:
        return '18-30';
      case 0:
        return '<18';
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/session.service.ts